import React from "react";
import PropTypes from "prop-types";
import { prProfileUrl, companyUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import ListRow from "../../profiles/ListRow";

function companyHref(slug, migrateId) {
  return slug ? companyUrl({ slug, migrate_id: migrateId }) : undefined;
}

/**
 * RepresentedByCard — the Expert/Hybrid profile's "Represented by" rail card: the PR profiles
 * who represent this expert entity. Relocated out of the main-column section stack — it now
 * sits third in the rail, after Connect — same row content as before, just a rail card rather
 * than a `<SectionCard>` sitting between Employment and Represents.
 *
 * Purely presentational and self-collapsing: nothing at all once the provider settles with zero
 * representatives (mirrors TeamMembersCard's collapse pattern). The provider loads `representedBy`
 * as part of the page's single fatal load, so — unlike TeamMembersCard/MediaOutletsRailCard, which
 * self-fetch on their own idle/settle cycle — this card takes no `isLoading` of its own: the page
 * level skeleton already covers the load it depends on.
 *
 * @param {Object} props
 * @param {Array} [props.representedBy] - PR profiles representing this expert (`{ id, name, slug,
 *   profileImageUrl, title, companyName, companySlug }`)
 */
function RepresentedByCard({ representedBy = [] }) {
  const reps = representedBy || [];
  if (reps.length === 0) return null;

  return (
    <SectionCard id="expert-rail-represented-by" title="Represented by">
      {/* The row renders only itself, so this list owns the items and the n-1 dividers between them. */}
      <ul className="list-unstyled mb-0">
        {reps.map((rep, index) => (
          <li key={rep.id} className={`py-2${index < reps.length - 1 ? " border-bottom" : ""}`}>
            <ListRow
              href={prProfileUrl({ slug: rep.slug, migrate_id: rep.migrateId })}
              imageUrl={rep.profileImageUrl}
              title={rep.name}
              subtitleTitle={rep.title}
              subtitleCompanyName={rep.companyName}
              subtitleCompanyHref={companyHref(rep.companySlug, rep.companyMigrateId)}
            />
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

RepresentedByCard.propTypes = {
  representedBy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      slug: PropTypes.string,
      profileImageUrl: PropTypes.string,
      title: PropTypes.string,
      companyName: PropTypes.string,
      companySlug: PropTypes.string,
    }),
  ),
};

export default RepresentedByCard;
